import { Injectable } from '@angular/core';
import { FileOpener } from '@ionic-native/file-opener/ngx';
// Avoid collision with HTML5 File model
import { File as CordovaFile, FileEntry } from '@ionic-native/file/ngx';
import { Platform } from '@ionic/angular';
import * as moment from 'moment';
import { DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';
import { Base64File } from '../../models/payment-plan';
import { Document } from '../../models/policy';
import {
  convertb64toBlob, convertBlobTob64,

  createAnchorTag
} from '../../utils/utils';
import { FileManagerService } from '../core/file-manager.service';
import { LogService } from '../core/log.service';


@Injectable({
  providedIn: 'root'
})
export class FileHelperService {
  private defaultFileExtensions = [
    'image/tif',
    'image/tiff',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf'
  ];

  private temporaryFilesDirectory: string;
  private TEMPORARY_FILES = 'TEMPORARY_FILES';

  constructor(
    private platform: Platform,
    private file: CordovaFile,
    private imageCompress: NgxImageCompressService,
    private fileOpener: FileOpener,
    private fileManager: FileManagerService,
    private log: LogService
  ) {
    this.platform.ready().then(() => {
      if (this.platform.is('cordova')) {
        this.temporaryFilesDirectory = this.file.cacheDirectory;
      }
    });
  }

  async openBase64(
    base64: string,
    fileName?: string,
    mimeType = 'application/pdf'
  ) {
    if (!fileName) {
      fileName = `downloaded_${new Date().getTime()}`;
    }
    const document = await this.getDocumentFromBase64(base64, mimeType, fileName)
      .catch(() => null);
    await this.open(document).catch(() => null);
  }

  async open(file: Base64File) {
    if (!file || !file.base64Content || !file.mimeType) {
      return;
    }
    const blob = convertb64toBlob(file.base64Content, file.mimeType);
    if (this.platform.is('cordova')) {
      const fileName = this.generateFileName(file);
      const resFileEntry: FileEntry = await this.createTemporaryFile(
        fileName,
        blob
      ).catch(err => {
        this.log.e(this, 'Error creating temporary file');
        throw err;
      });
      await this.fileOpener
        .open(resFileEntry.nativeURL, file.mimeType)
        .catch(err => {
          this.log.e(
            this,
            'Error opening file in system with fileOpener: ',
            err
          );
          this.removeTemporaryFile(fileName).catch(fileRemovingError => {
            this.log.e(
              this,
              'Error removing temporary file: ',
              fileRemovingError
            );
          });
        });
    } else {
      createAnchorTag(blob, file.name);
    }
  }

  private createTemporaryFile(fileName: string, blob: Blob) {
    return this.fileManager
      .writeFile(blob, {
        systemDir: this.temporaryFilesDirectory,
        path: this.TEMPORARY_FILES,
        fileName,
        replace: true
      })
      .toPromise();
  }

  private removeTemporaryFile(fileName: string) {
    return this.fileManager
      .removeFile({
        systemDir: this.temporaryFilesDirectory,
        path: this.TEMPORARY_FILES,
        fileName
      })
      .toPromise();
  }

  cleanTemporaryFiles() {
    return this.fileManager
      .removeDir({
        systemDir: this.temporaryFilesDirectory,
        path: this.TEMPORARY_FILES,
        recursive: true
      })
      .toPromise();
  }

  async fileToPolicyDocument(file: File): Promise<Document> {
    const base64Cont = await convertBlobTob64(file);
    return {
      name: file.name ? file.name : '',
      mimeType: file.type ? file.type : '',
      base64Content: base64Cont,
      size: file.size ? file.size : 0
    };
  }

  async getDocumentFromBase64(
    base64Cont: string,
    mimeType?: string,
    name?: string
  ) {
    const blob = convertb64toBlob(base64Cont, mimeType);
    return {
      name: name ? name : '',
      mimeType: blob.type ? blob.type : '',
      base64Content: base64Cont,
      size: blob.size ? blob.size : 0
    };
  }

  isValidFileExtension(mimeType: string, extensions?: string[]) {
    if (extensions && extensions.length) {
      return extensions.some(extension => extension === mimeType);
    }
    return this.defaultFileExtensions.some(extension => extension === mimeType);
  }

  generateFileName({ mimeType, name }: Partial<Base64File>) {
    const hasExtension = (name || '').split('.').length > 1;
    if (hasExtension) {
      return name;
    }

    const extension = this.getFileNameExtensionByMimeType(mimeType);
    const currentTime = moment().format('YYYYMMDDHHmmss');
    const documentTypePrefix = this.isMimeTypeImage(mimeType)
      ? 'photo'
      : 'document';
    const defaultName = name || `${documentTypePrefix}_${currentTime}`;

    return [defaultName, extension]
      .filter(Boolean)
      .join('.');
  }

  getFileNameExtensionByMimeType(mimeType: string) {
    if (!mimeType) {
      return '';
    }
    if (this.isMimeTypeImage(mimeType)) {
      if (mimeType === 'image/x-windows-bmp') {
        return 'xbm';
      } else {
        const components = mimeType.split('/');
        return components && components.length > 1 ? components[1] : '';
      }
    }
    switch (mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      case 'application/msword':
        return 'doc';
      default:
        return '';
    }
  }

  getFileExtensionByFileName(fileName: string) {
    const components = fileName.split('.');
    return components && components.length > 1 ? components[components.length - 1] : '';
  }

  getFileTypeByExtension(extension: string): 'document' | 'image' | 'pdf' | '' {
    const image = ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'bmp'];
    const document = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

    if (extension === 'pdf') {
      return 'pdf';
    }

    if (image.includes(extension)) {
      return 'image';
    }

    if (document.includes(extension)) {
      return 'document';
    }

    return '';
  }

  isMimeTypeImage(mimeType: string) {
    return mimeType && mimeType.startsWith('image/');
  }

  calculateDocumentsWeight(documents) {
    return documents.reduce((prev, curr) => {
      return prev + curr.size;
    }, 0);
  }

  compressBase64Image(base64: string, mimeType: string) {
    const cleanImageBase64 = base64.replace(/^data:(.*;base64,)?/, '');
    return this.imageCompress
      .compressFile(
        `data:${mimeType};base64,${cleanImageBase64}`,
        DOC_ORIENTATION.Up,
        80,
        50
      )
      .then(res => {
        return res.toString().replace(/^data:(.*;base64,)?/, '');
      });
  }
}
